import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18n from 'src/utils/translate';
import styled from 'styled-components';
import config from 'config/config';
import Router from 'next/router';

import { fetchNotifications } from 'src/modules/shared/context/actions';
import { logout } from 'src/modules/shared/auth/actions';
import { showSignupModal, showLoginModal } from 'src/modules/shared/modal/actions';
import Badge from 'src/components/badges/badge';

// Sitebar subcomponents
import VisitorSiteBar from './visitorSiteBar';
import SearchBar from './searchBar';
import UserMenu from './userMenu'; // logged in, desktop
import HubMenu from './hubMenu'; // logged in, desktop
import NotificationMenu from './notificationMenu'; // logged in, desktop
import MobileSiteBar from './mobileSiteBar'; // logged in, mobile
import { footfall, footfallExternal } from './sitebarFootfall';

const isServer = typeof window === 'undefined';

const SpeechBubbleLogo = styled.img`
  width: 32px;
  height: 32px;
  margin-left: -5px;
`;

const initialMenuState = {
  openUserMenu: false,
  openHubMenu: false,
  openNotifications: false,
};

const getCurrentRoute = () => {
  if (isServer) return null;
  return Router.route;
};

class SiteBar extends Component {
  static propTypes = {
    basic: PropTypes.bool,
    currentUser: PropTypes.object,
  }

  static defaultProps = {
    basic: false,
    currentUser: {},
    fetchNotifications: () => {},
  }

  state = { ...initialMenuState }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  setMenuWrapperRef = (node) => {
    this.menuWrapperRef = node;
  }

  handleClickOutside = (event) => {
    if (this.menuWrapperRef && !this.menuWrapperRef.contains(event.target)) {
      this.setState(initialMenuState);
    }
  }

  toggleMenuHideOthers = (event, menuToToggle) => {
    if (event) event.preventDefault();
    const stateObj = { ...initialMenuState };
    if (menuToToggle) stateObj[menuToToggle] = !this.state[menuToToggle];
    this.setState(stateObj);
    if (event) event.stopPropagation();
  }

  onNotificationsClick = (event) => {
    if (!this.state.openNotifications) {
      this.props.fetchNotifications();
    }
    this.toggleMenuHideOthers(event, 'openNotifications');
  }

  renderMemberSitebar() {
    const {
      unreadNotificationsCount,
      unreadMessagesCount,
      currentUser: user,
      logout,
      notifications,
    } = this.props;
    const followedCommunities = user.followedCommunities;
    const homeClass = getCurrentRoute() === '/home/explore' ? 'sitebar__link sitebar__link--active' : 'sitebar__link';

    return (
      <nav role="navigation">
        <MobileSiteBar
          currentUser={user}
          unreadMessages={unreadMessagesCount}
          unreadNotifications={unreadNotificationsCount}
        />
        <div className="sitebar">
          <div className="sitebar-container hidden-xs" ref={this.setMenuWrapperRef}>
            <div className="sitebar-actions-left">
              <a
                className={homeClass}
                href="/"
                onClick={(e) => footfallExternal(e, '/', 'home')}
              >
                <SpeechBubbleLogo alt="HealthUnlocked small logo" src={`${config.cdn.static}img/logos/H-logo-speech-bubble-green.svg`} />
                {i18n.t('My feed')}
              </a>

              <HubMenu
                followedCommunities={followedCommunities}
                handleClick={(e) => {
                  footfall('my-hub');
                  this.toggleMenuHideOthers(e, 'openHubMenu');
                }}
                open={this.state.openHubMenu}
                userId={user.userId}
              />

              <a
                className="sitebar__link"
                href="/messages"
                onClick={(e) => footfallExternal(e, '/messages', 'chat')}
              >
                <span className="icon-paper-plane" />
                {i18n.t('Chat')}
                {unreadMessagesCount > 0 && <Badge content={unreadMessagesCount} type="new" />}
              </a>

              <NotificationMenu
                handleClick={(e) => {
                  footfall('alerts');
                  this.onNotificationsClick(e);
                }}
                notifications={notifications}
                open={this.state.openNotifications}
                unreadNotifications={unreadNotificationsCount}
              />
            </div>

            <div className="site-logo">
              <a href="/" onClick={(e) => footfallExternal(e, '/', 'hu-logo')}>
                <img
                  alt="HealthUnlocked logo"
                  height="54"
                  src={`${config.cdn.static}img/logos/healthunlocked-logo.svg`}
                  width="214"
                />
              </a>
            </div>

            <div className="sitebar-actions-right">
              <SearchBar
                className="search-bar--navbar search-bar--navbar-member"
                customId="-sitebar"
                onClick={() => footfall('search')}
              />

              <UserMenu
                handleClick={e => {
                  footfall('more');
                  this.toggleMenuHideOthers(e, 'openUserMenu');
                }}
                logout={logout}
                open={this.state.openUserMenu}
                user={user}
              />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  render() {
    if (this.props.isLoggedIn) {
      return this.renderMemberSitebar();
    } else {
      return <VisitorSiteBar />;
    }
  }
}

const mapStateToProps = (state) => {
  return {
    isLoggedIn: !!state.context.currentUser,
    currentUser: state.context.currentUser,
    unreadNotificationsCount: state.context.unreadNotificationsCount,
    unreadMessagesCount: state.context.unreadMessagesCount,
    notifications: state.context.notifications,
  };
};

export default connect(mapStateToProps, { logout, fetchNotifications, showLoginModal, showSignupModal })(SiteBar);
